import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Wrench, Calculator, BarChart3, Newspaper, BookOpen, Plus, Save, Edit3, Trash2, ChevronRight, FolderPlus, Image, NotebookPen } from "lucide-react";

const BUILT_IN_TOOLS = [
  { slug: "trading-journal", title: "Trading Journal", description: "Il tuo diario di trading privato: registra e analizza i tuoi trade", icon: NotebookPen, color: "#F59E0B", builtin: true },
  { slug: "calcolatore-rischio", title: "Calcolatore Rischio", description: "Calcola i lottaggi in base a balance, SL e rischio", icon: Calculator, color: "#2FD8A8", builtin: true },
  { slug: "interesse-composto", title: "Interesse Composto", description: "Simula la crescita del tuo capitale nel tempo", icon: BarChart3, color: "#64C8FF", builtin: true },
  { slug: "calendario-economico", title: "Calendario Economico", description: "News economiche in tempo reale con filtri personalizzabili", icon: Newspaper, color: "#EF4444", builtin: true },
];

export default function Strumenti() {
  const { isAdmin } = useAuth();
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSub, setNewSub] = useState({ title: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });

  useEffect(() => { fetchSubs(); }, []);

  const fetchSubs = async () => {
    try {
      const res = await axios.get(`${API}/content/strumenti-subcategories`);
      setSubcategories(res.data.map(s => {
        try { return { ...s, ...JSON.parse(s.content) }; }
        catch { return s; }
      }).sort((a, b) => a.order - b.order));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const addSub = async () => {
    if (!newSub.title.trim()) { toast.error("Inserisci un titolo"); return; }
    const slug = newSub.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    try {
      await axios.post(`${API}/content`, {
        section: "strumenti-subcategories",
        content_type: "subcategory",
        title: newSub.title,
        content: JSON.stringify({ slug, description: newSub.description }),
        order: subcategories.length
      });
      toast.success("Sottocategoria aggiunta");
      setNewSub({ title: "", description: "" });
      setShowAdd(false);
      fetchSubs();
    } catch { toast.error("Errore"); }
  };

  const startEdit = (sub, e) => {
    e.preventDefault(); e.stopPropagation();
    setEditingId(sub.id);
    setEditForm({ title: sub.title, description: sub.description || "" });
  };

  const saveEdit = async (sub) => {
    const slug = editForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    try {
      await axios.put(`${API}/content/${sub.id}`, {
        title: editForm.title,
        content: JSON.stringify({ slug, description: editForm.description })
      });
      toast.success("Aggiornato");
      setEditingId(null);
      fetchSubs();
    } catch { toast.error("Errore"); }
  };

  const deleteSub = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Eliminato");
      fetchSubs();
    } catch { toast.error("Errore"); }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="strumenti-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="Toolkit" title="Strumenti" subtitle="Calcolatori, risorse e strumenti per potenziare il tuo trading" />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        {/* Built-in Tools — numbered rows */}
        <div className="max-w-5xl mx-auto mb-14">
          {BUILT_IN_TOOLS.map((tool, idx) => {
            const Icon = tool.icon;
            return (
              <Link
                key={tool.slug}
                to={`/strumenti/${tool.slug}`}
                className="tool-row group"
                data-testid={`tool-${tool.slug}`}
              >
                <div className="w-14 h-14 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${tool.color}20` }}>
                  <Icon size={26} style={{ color: tool.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{tool.title}</h2>
                  <p className="text-sm" style={{ color: "var(--mm-text-secondary)" }}>{tool.description}</p>
                </div>
                <ChevronRight size={24} className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1" style={{ color: tool.color }} />
              </Link>
            );
          })}
        </div>

        {/* Custom Subcategories */}
        {(subcategories.length > 0 || isAdmin) && (
          <>
            <div className="flex items-center gap-4 max-w-4xl mx-auto mb-6">
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
              <span className="text-sm font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>RISORSE</span>
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
            </div>

            {isAdmin && (
              <div className="max-w-lg mx-auto mb-6">
                {!showAdd ? (
                  <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2 mx-auto text-sm" data-testid="add-strumenti-sub-btn">
                    <FolderPlus size={16} /> Aggiungi Categoria Risorse
                  </button>
                ) : (
                  <div className="glass-card p-4 space-y-3">
                    <input type="text" value={newSub.title} onChange={(e) => setNewSub({ ...newSub, title: e.target.value })} className="admin-input" placeholder="Nome categoria" />
                    <input type="text" value={newSub.description} onChange={(e) => setNewSub({ ...newSub, description: e.target.value })} className="admin-input" placeholder="Descrizione (opzionale)" />
                    <div className="flex gap-3">
                      <button onClick={addSub} className="btn-primary flex-1 py-2 text-sm"><Save size={14} className="inline mr-1" />Salva</button>
                      <button onClick={() => { setShowAdd(false); setNewSub({ title: "", description: "" }); }} className="btn-secondary flex-1 py-2 text-sm">Annulla</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {subcategories.map((sub, index) => {
                if (editingId === sub.id) {
                  return (
                    <div key={sub.id} className="glass-card p-4 space-y-3">
                      <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input text-sm" />
                      <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="admin-input text-sm" placeholder="Descrizione" />
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(sub)} className="btn-primary flex-1 py-1.5 text-sm">Salva</button>
                        <button onClick={() => setEditingId(null)} className="btn-secondary flex-1 py-1.5 text-sm">Annulla</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={sub.id}
                    to={`/strumenti/${sub.slug}`}
                    className="glass-card p-4 sm:p-5 flex flex-col items-center text-center group transition-all duration-300 hover:scale-[1.03] relative"
                    style={{ borderColor: "rgba(47, 216, 168, 0.15)", minHeight: "120px" }}
                    data-testid={`strumenti-sub-${index}`}
                  >
                    {isAdmin && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button onClick={(e) => startEdit(sub, e)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(47,216,168,0.2)" }}><Edit3 size={12} style={{ color: "var(--mm-gold)" }} /></button>
                        <button onClick={(e) => deleteSub(sub.id, e)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(239,68,68,0.2)" }}><Trash2 size={12} style={{ color: "#EF4444" }} /></button>
                      </div>
                    )}
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: "rgba(47, 216, 168, 0.1)" }}>
                      <Image size={24} style={{ color: "var(--mm-gold)" }} />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{sub.title}</h3>
                    {sub.description && <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>{sub.description}</p>}
                    <div className="flex items-center gap-1 mt-auto pt-2" style={{ color: "var(--mm-gold)" }}>
                      <span className="text-xs font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>APRI</span>
                      <ChevronRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
